/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import {
  CartItemModel,
  NegotiableQuoteListEntry,
  NegotiableQuoteModel,
  NegotiableQuotesListModel,
  NegotiableQuoteStatus,
  PaginationInfo,
  ShippingAddress,
} from '@/quote-management/data/models/negotiable-quote-model';
import { state } from '@/quote-management/lib/state';
import { transformHistory } from './transform-history';
import { transformQuoteItems } from './transform-quote-items';

// Quote statuses that allow editing/sending for review
const EDITABLE_QUOTE_STATUSES = [
  'DRAFT',
  'UPDATED',
  'DECLINED',
  'EXPIRED',
  'NEW',
  'OPEN',
] as const;

// Helper function to get the total quantity of a quote (number of quote line items or total quantity)
function getTotalQuantity(quote: any) {
  if (!quote.items) return 0;

  const config = state.config;

  if (config?.quoteSummaryDisplayTotal === 0) return quote.items.length;
  if (config?.quoteSummaryDisplayTotal === 1) return quote.total_quantity;
  // Default to items count if unexpected config value or error returned
  return quote.items.length;
}

function checkAllItemsHaveStock(items: CartItemModel[]): boolean {
  return items.every((item) => !item.outOfStock && !item.insufficientQuantity);
}

// Base helper that transforms a single quote object directly
function transformSingleQuote(quote: any): NegotiableQuoteModel {
  const isQuoteNew = isNewQuote(quote);
  const isGeneratedFromTemplate = Boolean(quote.template_name?.trim());
  const quoteItems = transformQuoteItems(quote.items);
  return {
    uid: quote.uid,
    name: quote.name,
    createdAt: quote.created_at,
    updatedAt: quote.updated_at,
    expirationDate: quote.expiration_date,
    status: isQuoteNew ? NegotiableQuoteStatus.NEW : quote.status,
    isVirtual: Boolean(quote.is_virtual),
    salesRepName: quote.sales_rep_name,
    buyer: {
      firstname: quote.buyer.firstname,
      lastname: quote.buyer.lastname,
    },
    email: quote.email,
    templateName: quote.template_name,
    totalQuantity: getTotalQuantity(quote),
    comments: quote.comments?.map((comment: any) => {
      const baseComment: any = {
        uid: comment.uid,
        createdAt: comment.created_at,
        author: {
          firstname: comment.author.firstname,
          lastname: comment.author.lastname,
        },
        text: comment.text,
      };

      if (
        Array.isArray(comment.attachments) &&
        comment.attachments.length > 0
      ) {
        baseComment.attachments = comment.attachments.map((a: any) => ({
          name: a.name,
          url: a.url,
        }));
      }

      return baseComment;
    }),
    prices: quote.prices && {
      appliedDiscounts: quote.prices.discounts?.map((discount: any) => ({
        amount: {
          value: discount.amount.value,
          currency: discount.amount.currency,
        },
        label: discount.label,
        coupon: discount.coupon,
      })),
      appliedTaxes: quote.prices.applied_taxes?.map((tax: any) => ({
        amount: { value: tax.amount.value, currency: tax.amount.currency },
        label: tax.label,
      })),
      discount:
        quote.prices.discounts &&
        quote.prices.grand_total &&
        calculateTotal(
          quote.prices.discounts,
          quote.prices.grand_total.currency
        ),
      grandTotal: quote.prices.grand_total && {
        value: quote.prices.grand_total.value,
        currency: quote.prices.grand_total.currency,
      },
      grandTotalExcludingTax: quote.prices.grand_total_excluding_tax && {
        value: quote.prices.grand_total_excluding_tax.value,
        currency: quote.prices.grand_total_excluding_tax.currency,
      },
      subtotalExcludingTax: quote.prices.subtotal_excluding_tax && {
        value: quote.prices.subtotal_excluding_tax.value,
        currency: quote.prices.subtotal_excluding_tax.currency,
      },
      subtotalIncludingTax: quote.prices.subtotal_including_tax && {
        value: quote.prices.subtotal_including_tax.value,
        currency: quote.prices.subtotal_including_tax.currency,
      },
      subtotalWithDiscountExcludingTax: quote.prices
        .subtotal_with_discount_excluding_tax && {
        value: quote.prices.subtotal_with_discount_excluding_tax.value,
        currency: quote.prices.subtotal_with_discount_excluding_tax.currency,
      },
      ...transformShippingPrices(quote),
      totalTax:
        quote.prices.applied_taxes &&
        quote.prices.grand_total &&
        calculateTotal(
          quote.prices.applied_taxes,
          quote.prices.grand_total.currency
        ),
    },
    history: transformHistory(quote.history),
    items: quoteItems,
    shippingAddresses: quote.shipping_addresses?.map((address: any) => {
      const shippingAddress: ShippingAddress = {
        uid: address.uid,
        customerAddressUid: address.customer_address_uid,
        companyAddressId: address.company_address_id,
        firstname: address.firstname,
        lastname: address.lastname,
        company: address.company,
        street: address.street,
        city: address.city,
        postcode: address.postcode,
        country: {
          code: address.country.code,
          label: address.country.label,
        },
        telephone: address.telephone,
      };

      if (address.region) {
        shippingAddress.region = {
          code: address.region.code,
          label: address.region.label,
          regionId: address.region.region_id,
        };
      }

      return shippingAddress;
    }),
    canCheckout:
      ['UPDATED', 'DECLINED'].includes(quote.status) &&
      state.permissions.checkoutQuote &&
      quote.shipping_addresses?.length > 0 &&
      checkAllItemsHaveStock(quoteItems),
    canSendForReview:
      !isGeneratedFromTemplate &&
      (isQuoteNew || EDITABLE_QUOTE_STATUSES.includes(quote.status)) &&
      state.permissions.editQuote,
    canUpdateQuote:
      !isGeneratedFromTemplate &&
      (isQuoteNew || EDITABLE_QUOTE_STATUSES.includes(quote.status)) &&
      state.permissions.editQuote,
    canDelete:
      !isGeneratedFromTemplate &&
      !['PENDING', 'SUBMITTED', 'ORDERED'].includes(quote.status) &&
      state.permissions.deleteQuote,
    canClose: quote.status
      ? !isGeneratedFromTemplate &&
        !['DRAFT', 'CLOSED', 'ORDERED', 'OPEN'].includes(quote.status)
      : false,
    readOnly:
      isGeneratedFromTemplate ||
      (!isQuoteNew &&
        ['ORDERED', 'SUBMITTED', 'CLOSED', 'PENDING'].includes(quote.status)) ||
      !state.permissions.editQuote,
  };
}

export function transformQuote(quoteData: any): NegotiableQuoteModel | null {
  if (!quoteData) {
    return null;
  }

  return transformSingleQuote(quoteData);
}

// quote is new if it has a status of SUBMITTED and there is no history entry that
// has a change_type of UPDATED with a non-empty array of status changes
function isNewQuote(quote: any): boolean {
  return (
    quote.status === 'SUBMITTED' &&
    !(quote.history ?? []).some(
      (history: any) =>
        history.change_type === 'UPDATED' &&
        (history.changes?.statuses?.changes ?? []).length > 0
    )
  );
}

function transformNegotiableQuoteListEntry(
  quote: any
): NegotiableQuoteListEntry {
  const isQuoteNew = isNewQuote(quote);
  return {
    uid: quote.uid,
    name: quote.name,
    createdAt: quote.created_at,
    updatedAt: quote.updated_at,
    status: isQuoteNew ? NegotiableQuoteStatus.NEW : quote.status,
    buyer: {
      firstname: quote.buyer.firstname,
      lastname: quote.buyer.lastname,
    },
    templateName: quote.template_name,
    prices: {
      grandTotal: {
        value: quote.prices.grand_total.value,
        currency: quote.prices.grand_total.currency,
      },
    },
  };
}

export function transformNegotiableQuotesList(
  quotesData: any
): NegotiableQuotesListModel | null {
  if (!quotesData) {
    return null;
  }

  const transformedModel = {
    items:
      quotesData.items
        ?.filter((quote: any) => quote?.uid)
        .map(transformNegotiableQuoteListEntry) || [],
    pageInfo: {
      currentPage: quotesData.page_info.current_page,
      pageSize: quotesData.page_info.page_size,
      totalPages: quotesData.page_info.total_pages,
    },
    totalCount: quotesData.total_count,
    sortFields: quotesData.sort_fields
      ? {
          default: quotesData.sort_fields.default,
          options: quotesData.sort_fields.options,
        }
      : undefined,
  };

  // Calculate pagination info
  const paginationInfo = transformPaginationInfo(transformedModel);

  return {
    ...transformedModel,
    paginationInfo: paginationInfo || undefined,
  };
}

export function transformPaginationInfo(
  quotesData: NegotiableQuotesListModel | null
): PaginationInfo | null {
  if (!quotesData?.pageInfo || !quotesData.totalCount) {
    return null;
  }

  const { currentPage, pageSize, totalPages } = quotesData.pageInfo;
  const { totalCount } = quotesData;

  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const pageSizeOptions = [20, 30, 50, 100, 200]; // Default page size options

  return {
    currentPage,
    totalCount,
    pageSize,
    startItem,
    endItem,
    totalPages,
    pageSizeOptions,
  };
}

// TODO: Check if admin has this configuration
export const getDefaultPageSizeOptions = () => [20, 30, 50, 100, 200];

export function calculateTotal(data: any[], currency: string) {
  if (!data?.length)
    return {
      value: 0,
      currency,
    };

  return data.reduce(
    (acc: any, item: any) => {
      return {
        value: acc.value + item.amount.value,
        currency: item.amount.currency,
      };
    },
    { value: 0, currency }
  );
}

export function transformShippingPrices(quote: any) {
  if (!quote || !quote.shipping_addresses?.length) {
    return {};
  }

  const shippingAddress = quote.shipping_addresses[0];
  const selectedShippingMethod = shippingAddress.selected_shipping_method;

  if (!selectedShippingMethod) {
    return {};
  }

  return {
    shippingIncludingTax: selectedShippingMethod.price_incl_tax && {
      value: selectedShippingMethod.price_incl_tax.value,
      currency: selectedShippingMethod.price_incl_tax.currency,
    },
    shippingExcludingTax: selectedShippingMethod.price_excl_tax && {
      value: selectedShippingMethod.price_excl_tax.value,
      currency: selectedShippingMethod.price_excl_tax.currency,
    },
  };
}
